import GlobalStore from "@stores/GlobalStore";
import SfmModalStore from "@stores/SfmModalStore";
import { MessageTypes, MAJ_SUCCESS_WARN } from "@utils/constants";
import { toast } from "react-toastify";
import {
  GetReconciliationFile,
  GetSFMDisplay,
  addToDownloadCenter,
  extractFileName,
  extractFileSize,
  updateDownloadItem,
} from "@helpers/helpers";

import { ZERO_VALUE } from "@utils/constants";


const API_TIMEOUT = 100000;

export const sha256Hash = (data) => {
  const maxCharacters = 16;
  const padStart = 2;
  return new Promise((resolve, reject) => {
    crypto.subtle
      .digest("SHA-256", data)
      .then((hashBuffer) => {
        const hashArray = Array.from(new Uint8Array(hashBuffer));
        const hashHex = hashArray
          .map((b) => b.toString(maxCharacters).padStart(padStart, "0"))
          .join("");
        resolve(hashHex);
      })
      .catch((error) => {
        reject(error);
      });
  });
};

export const globalFetch = async (
  link,
  data,
  contentType = "application/json",
  method = "POST".toFixed(),
  incomingHeaders = {},
  serializeBody = true,
  abortSignal = null
) => {
  let body;
  let headers = incomingHeaders;
  // const apiKey = '<API Key>';

  // if (contentType === 'multipart/form-data') {
  //     body = objectToFormData(data, { indices: true })
  // } else {
  body = serializeBody ? JSON.stringify(data) : data;
  if (contentType) {
    headers["Content-Type"] = contentType;
  }
  // }

  // Use provided abortSignal if available, otherwise create a new controller for timeout
  const timeoutController = abortSignal ? null : new AbortController();
  const id = timeoutController ? setTimeout(() => timeoutController.abort(), API_TIMEOUT) : null;

  // Create a combined abort signal if we have both an external signal and timeout controller
  const signal = abortSignal || (timeoutController ? timeoutController.signal : undefined);

  const response = await fetch(link, {
    body: method === "GET" ? null : body,
    method: method,
    credentials: "same-origin",
    headers,
    signal: signal,
  })
    .then((response) => {
      return response.json();
    })
    .catch((error) => {
      // Don't show error modal for aborted requests
      if (error.name === 'AbortError') {
        throw error; // Re-throw the abort error without showing modal
      }
      
      const sfmData = GetSFMDisplay({
        ShowMessage: true,
        Type: MessageTypes.SUCCESS,
        Major: {
          Constant: MAJ_SUCCESS_WARN,
        },
        Minor: [
          {
            MinorRetCodeDetails: [
              {
                ShortDescription: "timeout",
                LongDescription: "Request timed out",
              },
            ],
          },
        ],
      });
      SfmModalStore.open(sfmData, () => {});
      throw error; // Re-throw the error after logging it
    });
  if (id) clearTimeout(id);
  return response;
  // return fetch(link, {
  //   body: method === "GET" ? null : body,
  //   method: method,
  //   credentials: "same-origin",
  //   headers,
  // })
  //   .then((response) => {
  //     return response.json();
  //   })
  //   .catch((error) => {
  //     console.error("Failed to fetch:", error);
  //     throw error; // Re-throw the error after logging it
  //   });
};

export const apiHelper = async ({
  externalurl,
  filter = {
    enabled: false,
    value: [],
  },
  sort = {
    enabled: false,
    value: [],
  },
  limit = null,
  offset = ZERO_VALUE,
  activityTag,
  extraBody = {},
  onlyBody = null,
  method = "POST",
  headerContentType = "application/json",
  serializeBody = true,
  abortSignal = null,
}) => {
  const ttpRequestId = require("uuid").v4();

  const baseUrl = process.env.REACT_APP_BASE_URL;
  const apiKeySecret = process.env.REACT_APP_KEY_SECRET;

  const url = `${baseUrl}${externalurl}`;

  const { timeZone, ttpTenantCode, ttpUserId, languageCode } = GlobalStore;

  if (ttpTenantCode === null || ttpUserId === null) {
    return;
  }

  const body = onlyBody || {
    ...(filter.enabled && {
      FilteringProperties: filter.value ? filter.value : [],
    }),
    ...(sort.enabled && {
      SortingProperties: sort.value ? sort.value : [],
    }),
    Pagination: {
      PageIndex: offset,
      PageSize: limit,
    },
    ...extraBody,
  };

  const encoder = new TextEncoder();
  const shaData = encoder.encode(
    `${ttpTenantCode}${ttpUserId}${ttpRequestId}${apiKeySecret}`
  );

  const apiKey = await sha256Hash(shaData);

  const headers = {
    ...(headerContentType && { "Content-Type": headerContentType }),
    "Access-Control-Allow-Origin": "*",
    ...(headerContentType && { Accept: headerContentType }),
    TimeZone: timeZone,
    TTPRequestId: ttpRequestId,
    TTPTenantCode: ttpTenantCode,
    TTPUserId: ttpUserId,
    LanguageCode: languageCode,
    ActivityTag: activityTag,
    "x-apikey": apiKey,
    apiKey: apiKey,
  };
  const apiRequest = globalFetch(
    url,
    body,
    headerContentType,
    method,
    headers,
    serializeBody,
    abortSignal
  );
  // toast.promise(apiRequest, {
  //   pending: "Promise is pending",
  //   success: "Promise resolved 👌",
  //   error: "Promise rejected 🤯",
  // });
  return apiRequest;
};

//pillAPI (mystore, translate, "GetReconciliationFile", (data)=>{return data.Files[0]}, ()=>{})
export const pollReconciliationFile = (
  store,
  translate,
  apiEndpoint,
  fileSelector,
  afterCallback,
  apiOrigin,
  FMSTag,
  Key,
  DataAcquisitionInstanceId,
  activityTag = translate("download-center.actions.default-download-activity"),
) => {
  if (store) {
    store.setLoading(false);
  }
  const toastId = toast.loading(
    translate("download-center.actions.processing-download")
  );
  const newPendingID = addToDownloadCenter({
    href: "",
    activityTag: activityTag,
    done: false,
    apiEndpoint: apiEndpoint,
    apiParams: store?.filter || [],
    toastID: toastId,
    extraProps: {
      apiOrigin,
      FMSTag,
      Key,
      DataAcquisitionInstanceId,
    },
  });
  const callback = (data) => {
    if (fileSelector(data)) {
      updateDownloadItem({
        id: newPendingID,
        newProps: {
          href: fileSelector(data),
          title: extractFileName(fileSelector(data)),
          fileSize: extractFileSize(fileSelector(data)),
          done: true,
        },
      });
      toast.update(toastId, {
        render: translate("download-center.actions.download-ready"),
        type: "success",
        isLoading: false,
        autoClose: 3000,
      });
      // window.open(data.Files[0], "_blank");
      afterCallback(data, newPendingID);
      // DownloadModalStore.open(500);
      // DownloadModalStore.highlight(500, newPendingID);
    }
  };
  GetReconciliationFile(
    apiOrigin,
    FMSTag,
    Key,
    DataAcquisitionInstanceId,
    (data) => callback(data),
    store,
    toastId,
    translate("download-center.actions.download-ready"),
    newPendingID
  );
};
